import React from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, List, ListItemButton, ListItemText, Stack, Switch, Typography, } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { clampStep, emptyOnboardingState, getTourProgress, onboardingStorageKey, parseOnboardingState, setTourProgress, } from './onboardingState';
import { eligibleAutomaticTour, nextAutomaticTour, toursForPage } from './onboardingTours';
import { TourActionRegistry } from './onboardingActions';
const OnboardingContext = React.createContext({
    openHelp: () => { }, startTask: () => { }, hasTask: () => false, registerSafeAction: () => () => { },
});
export const useOnboarding = () => React.useContext(OnboardingContext);
const loadState = (userId) => {
    if (userId === null)
        return emptyOnboardingState();
    try {
        return parseOnboardingState(localStorage.getItem(onboardingStorageKey(userId)));
    }
    catch (_a) {
        return emptyOnboardingState();
    }
};
function TourSpotlight({ tour, stepIndex, onNext, onPrevious, onPause, onSkip, onContents, canTryAction, onTryAction }) {
    const step = tour.steps[stepIndex];
    const location = useLocation();
    const [rect, setRect] = React.useState(null);
    const [anchorState, setAnchorState] = React.useState('missing');
    const [dataState, setDataState] = React.useState('ready');
    React.useLayoutEffect(() => {
        var _a;
        let frame = 0;
        let scrolledTarget = null;
        let observedTarget = null;
        const root = (_a = document.getElementById('app')) !== null && _a !== void 0 ? _a : document.body;
        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => scheduleMeasure()) : null;
        const measure = () => {
            var _a;
            const target = root.querySelector(`[data-onboarding-anchor="${step.anchor}"]`);
            if (target !== observedTarget) {
                resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
                if (target)
                    resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.observe(target);
                observedTarget = target;
            }
            if (!target) {
                setRect(null);
                setDataState('ready');
                setAnchorState('missing');
                return;
            }
            let box = target.getBoundingClientRect();
            const style = window.getComputedStyle(target);
            const visible = target.isConnected && style.display !== 'none' && style.visibility !== 'hidden'
                && box.width > 1 && box.height > 1;
            if (visible && scrolledTarget !== target) {
                target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                scrolledTarget = target;
                box = target.getBoundingClientRect();
            }
            const inViewport = visible && box.right > 0 && box.bottom > 0
                && box.left < window.innerWidth && box.top < window.innerHeight;
            setRect((current) => {
                if (!inViewport)
                    return null;
                if (current && current.top === box.top && current.left === box.left
                    && current.width === box.width && current.height === box.height)
                    return current;
                return { top: box.top, left: box.left, right: box.right, bottom: box.bottom, width: box.width, height: box.height };
            });
            const markedState = target.getAttribute('data-onboarding-state')
                || ((_a = target.querySelector('[data-onboarding-state]')) === null || _a === void 0 ? void 0 : _a.getAttribute('data-onboarding-state'));
            setDataState(markedState === 'loading' || markedState === 'empty' || markedState === 'error' ? markedState : 'ready');
            setAnchorState(inViewport ? 'visible' : 'hidden');
        };
        const scheduleMeasure = () => {
            if (frame)
                return;
            frame = window.requestAnimationFrame(() => { frame = 0; measure(); });
        };
        measure();
        const observer = new MutationObserver(scheduleMeasure);
        observer.observe(root, { subtree: true, childList: true, attributes: true,
            attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'data-onboarding-state'] });
        window.addEventListener('resize', scheduleMeasure);
        window.addEventListener('scroll', scheduleMeasure, true);
        return () => {
            observer.disconnect();
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
            if (frame)
                window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', scheduleMeasure);
            window.removeEventListener('scroll', scheduleMeasure, true);
        };
    }, [step.anchor, location.pathname]);
    const pad = 5;
    const top = rect ? Math.max(0, rect.top - pad) : 0;
    const left = rect ? Math.max(0, rect.left - pad) : 0;
    const right = rect ? Math.min(window.innerWidth, rect.right + pad) : 0;
    const bottom = rect ? Math.min(window.innerHeight, rect.bottom + pad) : 0;
    const mask = { position: 'absolute', bgcolor: 'rgba(8, 26, 17, 0.68)', pointerEvents: 'auto' };
    const cardWidth = Math.min(360, window.innerWidth - 24);
    const estimatedCardHeight = 270;
    const placeBelow = !!rect && bottom + estimatedCardHeight + 12 <= window.innerHeight;
    const placeAbove = !!rect && top - estimatedCardHeight - 12 >= 0;
    const placeBeside = !!rect && !placeBelow && !placeAbove;
    const sideLeft = left - cardWidth - 12 >= 12;
    const sideRight = right + cardWidth + 12 <= window.innerWidth - 12;
    const cardLeft = !rect ? (window.innerWidth - cardWidth) / 2
        : placeBeside && sideLeft ? left - cardWidth - 12
            : placeBeside && sideRight ? right + 12
                : Math.max(12, Math.min(left, window.innerWidth - cardWidth - 12));
    const cardTop = !rect ? Math.max(12, window.innerHeight / 2 - 110)
        : placeBelow ? bottom + 12
            : placeAbove ? top - estimatedCardHeight - 12
                : Math.max(12, Math.min(top, window.innerHeight - estimatedCardHeight - 12));
    const displayTitle = dataState === 'empty' ? step.emptyTitle || step.title : step.title;
    const displayDescription = dataState === 'loading' ? step.loadingDescription || 'Данные ещё загружаются.'
        : dataState === 'empty' ? step.emptyDescription || step.description
            : dataState === 'error' ? step.errorDescription || 'Не удалось загрузить данные. Попробуйте позже.'
                : step.description;
    return createPortal(React.createElement(Box, { sx: { position: 'fixed', inset: 0, zIndex: 5000, pointerEvents: 'none' } },
        rect ? React.createElement(React.Fragment, null,
            React.createElement(Box, { sx: Object.assign(Object.assign({}, mask), { top: 0, left: 0, right: 0, height: top }) }),
            React.createElement(Box, { sx: Object.assign(Object.assign({}, mask), { top, left: 0, width: left, height: bottom - top }) }),
            React.createElement(Box, { sx: Object.assign(Object.assign({}, mask), { top, left: right, right: 0, height: bottom - top }) }),
            React.createElement(Box, { sx: Object.assign(Object.assign({}, mask), { top: bottom, left: 0, right: 0, bottom: 0 }) }),
            React.createElement(Box, { sx: { position: 'absolute', top, left, width: right - left, height: bottom - top, border: '2px solid #9ee1b3', borderRadius: 1, pointerEvents: 'none', boxSizing: 'border-box' } })) : React.createElement(Box, { sx: Object.assign(Object.assign({}, mask), { inset: 0 }) }),
        React.createElement(Box, { role: "dialog", "aria-label": `Подсказка: ${displayTitle}`, sx: { position: 'absolute', top: cardTop, left: cardLeft, width: cardWidth, maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', p: 2, bgcolor: '#fff', color: 'text.primary', borderRadius: 2, boxShadow: 8, pointerEvents: 'auto' } },
            React.createElement(Typography, { variant: "caption", color: "primary.main", fontWeight: 700 },
                tour.title,
                " \u00B7 ",
                stepIndex + 1,
                " \u0438\u0437 ",
                tour.steps.length),
            React.createElement(Typography, { variant: "subtitle1", fontWeight: 700, sx: { mt: 0.5 } }, displayTitle),
            React.createElement(Typography, { variant: "body2", sx: { mt: 0.75 } }, displayDescription),
            anchorState !== 'visible' && React.createElement(Typography, { variant: "caption", color: "text.secondary", display: "block", sx: { mt: 1 } }, anchorState === 'missing' ? 'Элемент ещё не появился на странице. Можно подождать или пропустить шаг.' : 'Элемент сейчас скрыт. Можно пропустить этот шаг.'),
            step.safeActionKey && canTryAction(step.safeActionKey) && React.createElement(Button, { size: "small", variant: "outlined", onClick: () => onTryAction(step.safeActionKey), sx: { mt: 1 } }, "\u041F\u043E\u043F\u0440\u043E\u0431\u043E\u0432\u0430\u0442\u044C: \u043E\u0442\u043A\u0440\u044B\u0442\u044C \u0432\u043A\u043B\u0430\u0434\u043A\u0443"),
            React.createElement(Stack, { direction: "row", spacing: 0.5, justifyContent: "space-between", sx: { mt: 2 } },
                React.createElement(Button, { size: "small", onClick: onContents }, "\u041E\u0433\u043B\u0430\u0432\u043B\u0435\u043D\u0438\u0435"),
                React.createElement(Button, { size: "small", onClick: onPause }, "\u041F\u043E\u0437\u0436\u0435"),
                React.createElement(Button, { size: "small", onClick: onSkip }, "\u041F\u0440\u043E\u043F\u0443\u0441\u0442\u0438\u0442\u044C \u043E\u0431\u0437\u043E\u0440")),
            React.createElement(Stack, { direction: "row", spacing: 1, justifyContent: "flex-end", sx: { mt: 1 } },
                React.createElement(Button, { size: "small", startIcon: React.createElement(ArrowBackIcon, null), disabled: stepIndex === 0, onClick: onPrevious }, "\u041D\u0430\u0437\u0430\u0434"),
                React.createElement(Button, { variant: "contained", size: "small", endIcon: React.createElement(ArrowForwardIcon, null), onClick: onNext }, stepIndex === tour.steps.length - 1 ? 'Завершить' : anchorState === 'visible' ? 'Далее' : 'Пропустить шаг')))), document.body);
}
export function OnboardingProvider({ userId, role, children }) {
    var _a, _b, _c;
    const location = useLocation();
    const [state, setState] = React.useState(() => loadState(userId));
    const [helpOpen, setHelpOpen] = React.useState(false);
    const [helpTourId, setHelpTourId] = React.useState(null);
    const [stepsExpanded, setStepsExpanded] = React.useState(false);
    const [offerTourId, setOfferTourId] = React.useState(null);
    const [activeTourId, setActiveTourId] = React.useState(null);
    const automaticOfferConsumed = React.useRef(false);
    const automaticOfferId = React.useRef(null);
    const actions = React.useRef(new TourActionRegistry());
    const [actionRevision, setActionRevision] = React.useState(0);
    const registerSafeAction = React.useCallback((key, action) => {
        const unregister = actions.current.register(key, action);
        setActionRevision((current) => current + 1);
        return () => {
            unregister();
            setActionRevision((current) => current + 1);
        };
    }, []);
    const tours = React.useMemo(() => toursForPage(location.pathname, role), [location.pathname, role]);
    const activeTour = (_a = tours.find((tour) => tour.id === activeTourId)) !== null && _a !== void 0 ? _a : null;
    const offeredTour = (_b = tours.find((tour) => tour.id === offerTourId)) !== null && _b !== void 0 ? _b : null;
    const pageTours = tours.filter((tour) => tour.route !== null);
    const pageTour = (_c = pageTours.find((tour) => tour.kind !== 'feature')) !== null && _c !== void 0 ? _c : null;
    const helpTours = [...pageTours.filter((tour) => tour.kind === 'feature'), ...pageTours.filter((tour) => tour.kind !== 'feature'), tours[0]];
    const activeProgress = activeTour ? getTourProgress(state, activeTour.id) : null;
    const stepIndex = activeTour && activeProgress ? clampStep(activeProgress.stepIndex, activeTour.steps.length) : 0;
    React.useEffect(() => {
        var _a, _b;
        if (!activeTour)
            return;
        for (const key of (_b = (_a = activeTour.steps[stepIndex]) === null || _a === void 0 ? void 0 : _a.prepareActionKeys) !== null && _b !== void 0 ? _b : []) {
            actions.current.runOnce(key);
        }
    }, [activeTour === null || activeTour === void 0 ? void 0 : activeTour.id, stepIndex, actionRevision]);
    React.useEffect(() => {
        if (userId === null)
            return;
        try {
            localStorage.setItem(onboardingStorageKey(userId), JSON.stringify(state));
        }
        catch ( /* Private browsing may reject storage; the tour still works in memory. */_a) { /* Private browsing may reject storage; the tour still works in memory. */ }
    }, [state, userId]);
    React.useEffect(() => {
        var _a;
        if (activeTourId && !activeTour) {
            actions.current.restore();
            setActiveTourId(null);
        }
        if (!automaticOfferConsumed.current) {
            const first = nextAutomaticTour(state, location.pathname, role);
            if (first) {
                automaticOfferConsumed.current = true;
                automaticOfferId.current = first.id;
            }
        }
        const candidate = tours.find((tour) => tour.id === automaticOfferId.current && eligibleAutomaticTour(state, tour, role));
        setOfferTourId(activeTourId && activeTour ? null : (_a = candidate === null || candidate === void 0 ? void 0 : candidate.id) !== null && _a !== void 0 ? _a : null);
        // At most one automatic offer per mounted session; ordinary page tours stay in Help.
    }, [tours, state, role, activeTourId, activeTour]);
    React.useEffect(() => () => actions.current.restore(), []);
    const updateProgress = (tourId, patch) => setState((current) => setTourProgress(current, tourId, patch));
    const start = (tourId, index) => {
        const tour = tours.find((item) => item.id === tourId);
        if (!tour)
            return;
        actions.current.restore();
        updateProgress(tourId, { status: 'paused', prompted: true, stepIndex: clampStep(index, tour.steps.length) });
        setOfferTourId(null);
        setHelpOpen(false);
        setActiveTourId(tourId);
    };
    const pause = () => {
        actions.current.restore();
        if (activeTour)
            updateProgress(activeTour.id, { status: 'paused', prompted: true });
        setActiveTourId(null);
    };
    const skip = () => {
        actions.current.restore();
        if (activeTour)
            updateProgress(activeTour.id, { status: 'skipped', prompted: true });
        setActiveTourId(null);
    };
    const next = () => {
        if (!activeTour)
            return;
        if (stepIndex === activeTour.steps.length - 1) {
            actions.current.restore();
            updateProgress(activeTour.id, { status: 'completed', prompted: true, stepIndex });
            setActiveTourId(null);
        }
        else
            updateProgress(activeTour.id, { stepIndex: stepIndex + 1 });
    };
    const dismissOffer = () => {
        if (offeredTour)
            updateProgress(offeredTour.id, { prompted: true });
        setOfferTourId(null);
    };
    const disableAutomaticTours = () => {
        setState((current) => (Object.assign(Object.assign({}, (offeredTour ? setTourProgress(current, offeredTour.id, { prompted: true }) : current)), { autoDisabled: true })));
        setOfferTourId(null);
    };
    const openHelp = () => {
        var _a;
        setHelpTourId((_a = pageTour === null || pageTour === void 0 ? void 0 : pageTour.id) !== null && _a !== void 0 ? _a : tours[0].id);
        setStepsExpanded(false);
        setHelpOpen(true);
    };
    const openContents = () => {
        var _a, _b;
        setHelpTourId((_b = (_a = activeTour === null || activeTour === void 0 ? void 0 : activeTour.id) !== null && _a !== void 0 ? _a : pageTour === null || pageTour === void 0 ? void 0 : pageTour.id) !== null && _b !== void 0 ? _b : tours[0].id);
        setStepsExpanded(true);
        setHelpOpen(true);
    };
    const startTask = (taskKey) => {
        const tour = tours.find((item) => item.taskKey === taskKey);
        if (tour)
            start(tour.id, 0);
    };
    const hasTask = (taskKey) => tours.some((item) => item.taskKey === taskKey);
    return React.createElement(OnboardingContext.Provider, { value: { openHelp, startTask, hasTask, registerSafeAction } },
        children,
        activeTour && React.createElement(TourSpotlight, { tour: activeTour, stepIndex: stepIndex, onNext: next, onPrevious: () => updateProgress(activeTour.id, { stepIndex: stepIndex - 1 }), onPause: pause, onSkip: skip, onContents: openContents, canTryAction: (key) => actions.current.has(key), onTryAction: (key) => { actions.current.run(key); } }),
        React.createElement(Dialog, { open: !!offeredTour && !activeTour, onClose: dismissOffer, maxWidth: "xs", fullWidth: true },
            React.createElement(DialogTitle, null, (offeredTour === null || offeredTour === void 0 ? void 0 : offeredTour.kind) === 'feature' ? offeredTour.title : 'Познакомиться с платформой?'),
            React.createElement(DialogContent, null,
                React.createElement(Typography, { variant: "body2" }, (offeredTour === null || offeredTour === void 0 ? void 0 : offeredTour.kind) === 'feature'
                    ? `${offeredTour.announcement} Короткий обзор можно пропустить или открыть позже через «Помощь».`
                    : 'Короткий обзор покажет основные разделы платформы. Его можно остановить, пропустить или запустить позже через «Помощь».'),
                React.createElement(Button, { size: "small", onClick: disableAutomaticTours, sx: { mt: 1, px: 0 } }, "\u0411\u043E\u043B\u044C\u0448\u0435 \u043D\u0435 \u043F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0442\u044C \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438")),
            React.createElement(DialogActions, null,
                React.createElement(Button, { onClick: dismissOffer }, "\u041D\u0435 \u0441\u0435\u0439\u0447\u0430\u0441"),
                React.createElement(Button, { variant: "contained", onClick: () => offeredTour && start(offeredTour.id, 0) }, "\u041D\u0430\u0447\u0430\u0442\u044C \u043E\u0431\u0437\u043E\u0440"))),
        React.createElement(Dialog, { open: helpOpen, onClose: () => setHelpOpen(false), maxWidth: "xs", fullWidth: true, sx: { zIndex: 6000 } },
            React.createElement(DialogTitle, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 } },
                React.createElement(Typography, { variant: "h6", fontWeight: 700 }, "\u041F\u043E\u043C\u043E\u0449\u044C"),
                React.createElement(IconButton, { size: "small", "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u043F\u043E\u043C\u043E\u0449\u044C", onClick: () => setHelpOpen(false) },
                    React.createElement(CloseIcon, null))),
            React.createElement(DialogContent, { sx: { pt: '8px !important', pb: 2 } },
                React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 1.5 } }, "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u043A\u043E\u0440\u043E\u0442\u043A\u0438\u0439 \u043E\u0431\u0437\u043E\u0440. \u041F\u043E\u0434\u0441\u043A\u0430\u0437\u043A\u0438 \u043F\u043E\u044F\u0432\u044F\u0442\u0441\u044F \u043F\u0440\u044F\u043C\u043E \u043D\u0430 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0435."),
                React.createElement(Stack, { spacing: 0.75 }, helpTours.map((tour) => {
                    const progress = getTourProgress(state, tour.id);
                    const savedStep = clampStep(progress.stepIndex, tour.steps.length);
                    const selected = helpTourId === tour.id;
                    const expanded = stepsExpanded && helpTourId === tour.id;
                    const status = progress.status === 'paused' ? `Остановлено на шаге ${savedStep + 1}`
                        : progress.status === 'completed' ? 'Пройден'
                            : progress.status === 'skipped' ? 'Пропущен' : 'Не начат';
                    return React.createElement(Box, { key: tour.id, sx: { border: '1px solid', borderColor: selected ? 'primary.main' : 'divider', borderRadius: 1.5, overflow: 'hidden' } },
                        React.createElement(ListItemButton, { selected: selected, onClick: () => {
                                setHelpTourId(tour.id);
                                setStepsExpanded(false);
                            }, "aria-expanded": selected, sx: { py: 0.75, px: 1.25, '&.Mui-selected': { bgcolor: '#edf5ef' } } },
                            React.createElement(ListItemText, { primary: tour.title, secondary: `${tour.kind === 'feature' ? 'Новая функция' : tour.route ? 'Эта страница' : 'Вся платформа'} · ${tour.steps.length} ${tour.steps.length === 1 ? 'шаг' : tour.steps.length >= 5 ? 'шагов' : 'шага'} · ${status}`, primaryTypographyProps: { variant: 'subtitle2', fontWeight: 700 }, secondaryTypographyProps: { variant: 'caption' } }),
                            React.createElement(ExpandMoreIcon, { fontSize: "small", sx: { transform: selected ? 'rotate(180deg)' : 'none' } })),
                        React.createElement(Collapse, { in: selected },
                            React.createElement(Box, { sx: { px: 1.25, pb: 1, borderTop: '1px solid', borderColor: 'divider' } },
                                React.createElement(Stack, { direction: "row", spacing: 1, sx: { mt: 1 } },
                                    React.createElement(Button, { variant: "outlined", size: "small", onClick: () => start(tour.id, progress.status === 'paused' ? savedStep : 0) }, progress.status === 'paused' ? 'Продолжить' : progress.status === 'completed' ? 'Повторить' : 'Начать'),
                                    React.createElement(Button, { size: "small", onClick: () => setStepsExpanded((current) => !current), "aria-expanded": expanded, "aria-controls": `onboarding-help-steps-${tour.id}`, endIcon: React.createElement(ExpandMoreIcon, { sx: { transform: expanded ? 'rotate(180deg)' : 'none' } }), sx: { textTransform: 'none' } }, "\u0428\u0430\u0433\u0438")),
                                React.createElement(Collapse, { in: expanded },
                                    React.createElement(List, { id: `onboarding-help-steps-${tour.id}`, dense: true, sx: { mt: 1, maxHeight: 200, overflowY: 'auto', borderTop: '1px solid', borderColor: 'divider' } }, tour.steps.map((step, index) => (React.createElement(ListItemButton, { key: step.anchor, onClick: () => start(tour.id, index), selected: activeTourId === tour.id && index === stepIndex },
                                        React.createElement(ListItemText, { primary: `${index + 1}. ${step.title}` })))))))));
                })),
                React.createElement(Box, { sx: { mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' } },
                    React.createElement(FormControlLabel, { sx: { ml: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }, control: React.createElement(Switch, { size: "small", checked: state.autoDisabled, onChange: (event) => setState((current) => (Object.assign(Object.assign({}, current), { autoDisabled: event.target.checked }))) }), label: "\u041D\u0435 \u043F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0442\u044C \u043E\u0431\u0437\u043E\u0440\u044B \u0430\u0432\u0442\u043E\u043C\u0430\u0442\u0438\u0447\u0435\u0441\u043A\u0438" }),
                    location.pathname !== '/knowledge-base' && React.createElement(Button, { component: RouterLink, to: "/knowledge-base", size: "small", onClick: () => setHelpOpen(false), sx: { display: 'block', px: 0, mt: 0.5, textTransform: 'none' } }, "\u0411\u0430\u0437\u0430 \u0437\u043D\u0430\u043D\u0438\u0439")))));
}
